import axios, { AxiosError } from "axios";
import useAuthToken from "/@/composables/useAuthToken";
import useAuth from "/@/composables/useAuth";
import router from "/@/router/index";
import { isNetworkError, isTimeoutError } from "/@/utils/errorHandler";
import * as Sentry from "@sentry/vue";

export const http = axios.create({
  baseURL: String(import.meta.env.VITE_API_URL),
  timeout: 120000, // 120 seconds for file uploads
});

http.interceptors.request.use(
  async function (config) {
    const { getToken } = useAuthToken();

    const token = getToken();

    if (token) {
      config.headers["Authorization"] =
        config.headers["Authorization"] || `Bearer ${token}`;
    }

    // Add Sentry breadcrumb for API calls
    Sentry.addBreadcrumb({
      category: 'http',
      message: `${config.method?.toUpperCase()} ${config.url}`,
      level: 'info',
      data: {
        method: config.method,
        url: config.url,
        // DO NOT log request body (may contain PII)
      },
    });

    return config;
  },
  function (error) {
    return Promise.reject(error as AxiosError);
  }
);

http.interceptors.response.use(
  // eslint-disable-next-line
  function (config: any) {
    return config;
  },
  function (error) {
    const { logoutUser } = useAuth();

    // Handle network errors (no response object)
    if (!error.response) {
      // Add Sentry breadcrumb for network error
      Sentry.addBreadcrumb({
        category: 'http.error',
        message: 'Network error occurred',
        level: 'error',
        data: {
          errorMessage: error.message,
          errorCode: error.code,
        },
      });

      // Network timeout
      if (isTimeoutError(error)) {
        console.warn('[Axios] Request timeout detected');
        return Promise.reject(error);
      }

      // Network disconnection
      if (isNetworkError(error)) {
        console.warn('[Axios] Network error detected');
        return Promise.reject(error);
      }

      // Unknown error without response
      console.error('[Axios] Error without response:', error.message);
      return Promise.reject(error);
    }

    // Now safe to access error.response.status
    const status = error.response.status;

    // Add Sentry breadcrumb for HTTP errors
    Sentry.addBreadcrumb({
      category: 'http.error',
      message: `HTTP ${status} error`,
      level: status >= 500 ? 'error' : 'warning',
      data: {
        status,
        url: error.config?.url,
        method: error.config?.method,
        // DO NOT log response body (may contain PII)
      },
    });

    switch (status) {
      case 401:
        logoutUser();
        return Promise.reject(error);

      case 404:
        router.push({ name: "404" });
        return Promise.reject(error);

      case 500:
      case 502:
      case 503:
      case 504:
        // Server errors - log for monitoring
        console.error(`[Axios] Server error ${status}`);
        return Promise.reject(error);

      default:
        return Promise.reject(error);
    }
  }
);
